import React, { useState, useEffect, useContext, useRef } from 'react';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { api } from '../../services/api';
import { ModelCacheContext } from './ModelCacheContext';
import { WireframeOpacity } from './WireframeOpacity';

interface ModelProps {
  url: string;
  modelId: string;
  onDimensionsCalculated?: (dimensions: { x: number; y: number; z: number }) => void;
}

function Model({ url, modelId, onDimensionsCalculated }: ModelProps) {
  // Remove console log for rendering
  // console.log('Model component rendering for:', modelId);
  
  // Add error handling with errorMessage state
  const [errorLoading, setErrorLoading] = useState(false);
  const [refreshedUrl, setRefreshedUrl] = useState<string | null>(null);
  
  // Get the model cache
  const { cache, addToCache } = useContext(ModelCacheContext);
  
  // Check if this model is already in cache
  const isCached = !!cache.models[modelId];
  const cachedScene = isCached ? cache.models[modelId] : null;
  
  // Attempt to get a fresh URL on component mount if not cached
  useEffect(() => {
    const refreshUrl = async () => {
      // If we already have a cached model, use that
      if (isCached) {
        // console.log(`Using cached model for ${modelId}`);
        return;
      }
      
      try {
        if (modelId) {
          const freshUrl = await api.files.getDownloadUrl(modelId, 'glb');
          // console.log('Refreshed URL for model loading:', freshUrl);
          setRefreshedUrl(freshUrl);
        }
      } catch (error) {
        console.error('Failed to refresh URL:', error);
        setErrorLoading(true);
      }
    };
    
    refreshUrl();
  }, [modelId, isCached]);
  
  // Use the refreshed URL if available, otherwise fall back to the original URL or cached URL
  const finalUrl = isCached ? cache.urls[modelId] : (refreshedUrl || url);
  
  // Use try-catch with useGLTF to prevent crashes - only load when we have a URL and it's not cached
  const { scene } = !isCached && finalUrl ? useGLTF(finalUrl, undefined, undefined, (error) => {
    console.error("Error loading GLB:", error);
    setErrorLoading(true);
  }) : { scene: cachedScene };
  
  const modelRef = useRef<THREE.Object3D>(null);
  const [showDebug, setShowDebug] = useState(false);
  
  // Add to cache if we have a scene and it's not already cached
  useEffect(() => {
    if (scene && !isCached && finalUrl) {
      console.log(`Caching model ${modelId}`);
      addToCache(modelId, finalUrl, scene);
    }
  }, [scene, modelId, finalUrl, isCached, addToCache]);
  
  // Make sure to clear the GLTF cache when models are unloaded
  useEffect(() => {
    return () => {
      // This will be called when the component unmounts
      if (typeof finalUrl === 'string' && !isCached) {
        // Only clear from useGLTF cache if we're not caching it ourselves
        // Our own cache will handle this model
        useGLTF.preload(finalUrl);
        useGLTF.clear(finalUrl);
      }
    };
  }, [finalUrl, isCached]);
  
  // Calculate dimensions when the scene is loaded
  useEffect(() => {
    if (scene && onDimensionsCalculated) {
      // Create a bounding box for the entire scene
      const box = new THREE.Box3().setFromObject(scene);
      
      // Calculate dimensions
      const size = new THREE.Vector3();
      box.getSize(size);
      
      // Round to 1 decimal place and ensure positive values
      const dimensions = {
        x: Math.abs(Math.round(size.x * 10) / 10),
        y: Math.abs(Math.round(size.y * 10) / 10),
        z: Math.abs(Math.round(size.z * 10) / 10)
      };
      
      // Call the callback with the calculated dimensions
      onDimensionsCalculated(dimensions);
    }
  }, [scene, onDimensionsCalculated]);
  
  // Add a wireframes state array
  const [wireframes, setWireframes] = useState<THREE.LineSegments[]>([]);
  
  // Update the Model component to use this new component
  useEffect(() => {
    if (scene) {
      try {
        // Store references to added objects to remove them later
        const addedObjects: THREE.Object3D[] = [];
        const newWireframes: THREE.LineSegments[] = [];
        
        // Apply some default materials if needed
        scene.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh) {
            // Ensure materials render properly
            if (child.material) {
              // Create a solid base material
              const baseMaterial = new THREE.MeshBasicMaterial({
                color: new THREE.Color('#2FEB6C'),
                side: THREE.FrontSide,
                polygonOffset: true,      // Enable polygon offset to prevent z-fighting
                polygonOffsetFactor: 1,   // Move the material slightly away from the camera
                polygonOffsetUnits: 1,
                wireframe: false,
              });
              
              // Replace the original material with our base material
              if (Array.isArray(child.material)) {
                child.material = Array(child.material.length).fill(baseMaterial);
              } else {
                child.material = baseMaterial;
              }
              
              // Create a wireframe overlay with thinner lines
              const wireframeMaterial = new THREE.LineBasicMaterial({
                color: new THREE.Color('#000000'),  // Black color for wireframe
                linewidth: 1.0,  // Browser support for this is limited, but increase it anyway
                depthTest: true, // Use depth testing to hide back lines
                transparent: true,
                opacity: 0.95,
              });
              
              // Create wireframe geometry - using EdgesGeometry for cleaner look on complex models
              // Only display more pronounced edges (5 degree threshold instead of 10)
              const wireframeGeometry = new THREE.EdgesGeometry(child.geometry, 3);
              const wireframe = new THREE.LineSegments(wireframeGeometry, wireframeMaterial);
              
              // Make sure the wireframe is slightly offset to prevent z-fighting
              wireframe.position.copy(child.position);
              wireframe.rotation.copy(child.rotation);
              wireframe.scale.copy(child.scale);
              wireframe.renderOrder = 1; // Render after the base mesh
              
              // Add the wireframe to the scene
              child.parent?.add(wireframe);
              addedObjects.push(wireframe);
              newWireframes.push(wireframe);
            }
            
            // No need for shadows
            child.castShadow = false;
            child.receiveShadow = false;
          }
        });
        
        // Update wireframes state
        setWireframes(newWireframes);
        
        // Debug keyboard shortcut (Shift+D)
        const handleKeyDown = (e: KeyboardEvent) => {
          if (e.shiftKey && e.key === 'D') {
            setShowDebug(prev => !prev);
          }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => {
          window.removeEventListener('keydown', handleKeyDown);
          
          // Clean up added objects on unmount
          addedObjects.forEach(obj => {
            if (obj.parent) {
              obj.parent.remove(obj);
            }
            if (obj instanceof THREE.Mesh || obj instanceof THREE.LineSegments) {
              if (obj.geometry) {
                obj.geometry.dispose();
              }
              if (obj.material) {
                if (Array.isArray(obj.material)) {
                  obj.material.forEach(m => m.dispose());
                } else {
                  obj.material.dispose();
                }
              }
            }
          });
        };
      } catch (error) {
        console.error("Error setting up model:", error);
        setErrorLoading(true);
      }
    }
  }, [scene]);
  
  // Show a fallback if there's an error or still loading
  if (errorLoading || (!scene && !isCached && !finalUrl)) {
    return (
      <mesh>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#07F77F" />
      </mesh>
    );
  }
  
  return (
    <>
      {scene && <primitive ref={modelRef} object={scene} scale={1} />}
      {wireframes.length > 0 && (
        <WireframeOpacity wireframes={wireframes} modelRef={modelRef} />
      )}
    </>
  );
}

// Wrap the Model component with React.memo to prevent unnecessary re-renders
export const MemoizedModel = React.memo(Model);
export default Model; 